import React, { useMemo, useRef, useState, useEffect } from 'react';
import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import {
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  RefreshCw,
  Upload,
  Trash2,
  Brain,
  TrendingUp,
  TrendingDown,
  Lightbulb,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import PageLayout from '../components/layout/PageLayout';
import SkeletonCard from '../components/ui/SkeletonCard';
import { useAuth } from '../contexts/AuthContext';
import { statsApi, sessionsApi, trainingProgressApi } from '../lib/api';
import { queryKeys } from '../lib/queryKeys';
import { mockBankrollHistory } from '../mocks/bankroll.mock';
import { computeLevel } from '../mocks/level.mock';
import { extractInsights } from '../mocks/insights.mock';
import type { Session, TrainingProgressStorePayload } from '../types/api';

/* ═══ Font shorthand ═══ */
const F: React.CSSProperties = { fontFamily: 'Inter, sans-serif' };

/* ═══ Custom Tooltip ═══ */
function ChartTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: '#2a2a2a',
        border: '1px solid #333',
        borderRadius: 6,
        padding: '8px 12px',
        fontSize: 13,
        ...F,
      }}
    >
      <p style={{ color: '#555', marginBottom: 4 }}>{label}</p>
      {payload.map((p) => (
        <p key={p.name} style={{ color: p.color, fontWeight: 600, margin: '2px 0' }}>
          {p.name}: {p.value.toFixed(1)} BB
        </p>
      ))}
    </div>
  );
}

/* ═══ Upload steps ═══ */
const UPLOAD_STEPS = [
  'Lendo arquivo...',
  'Analisando mãos...',
  'Calculando EV...',
  'Gerando insights...',
];

interface DashboardLeakTrend {
  id: string;
  label: string;
  average: number;
  trend: 'improved' | 'worse' | 'stable';
  series: Array<{ slot: string; cost: number }>;
  summary: string;
}

function deriveLeakSignals(session: Session) {
  const hands = session.hands_total ?? session.stats?.ALL?.hands ?? 0;
  const ev = session.net_ev_bb ?? 0;
  const pressure = Math.max(0.2, Math.min(0.95, Math.abs(ev) / 6));

  return [
    {
      id: 'btn-fold',
      label: 'Fold no BTN',
      cost: Number((1.1 + pressure * 1.4 + (hands > 80 ? 0.2 : 0)).toFixed(1)),
    },
    {
      id: 'cbet-flop',
      label: 'C-bet flop',
      cost: Number((0.9 + pressure * 1.1 + (ev < 0 ? 0.35 : 0)).toFixed(1)),
    },
    {
      id: 'sb-preflop',
      label: 'SB pre-flop',
      cost: Number((0.7 + pressure * 0.9 + (hands < 50 ? 0.2 : 0)).toFixed(1)),
    },
  ];
}

function buildDashboardLeakTrends(sessions: Session[]): DashboardLeakTrend[] {
  if (!sessions.length) return [];

  const windowSessions = sessions.slice(0, 5).reverse();
  const template = deriveLeakSignals(sessions[0] ?? sessions[sessions.length - 1]);

  return template.map((item) => {
    const series = windowSessions.map((session, index) => {
      const leak = deriveLeakSignals(session).find((candidate) => candidate.id === item.id);
      return {
        slot: `S${index + 1}`,
        cost: leak?.cost ?? 0,
      };
    });

    const average = Number(
      (series.reduce((sum, point) => sum + point.cost, 0) / Math.max(series.length, 1)).toFixed(1)
    );
    const delta = Number((series[0].cost - series[series.length - 1].cost).toFixed(1));
    const trend =
      Math.abs(delta) < 0.15 ? 'stable' : delta > 0 ? 'worse' : 'improved';
    const summary =
      trend === 'stable'
        ? 'Consistencia semelhante na janela recente'
        : trend === 'improved'
        ? `Queda de ${Math.abs(delta).toFixed(1)} BB na leitura recente`
        : `Alta de ${delta.toFixed(1)} BB na leitura recente`;

    return {
      id: item.id,
      label: item.label,
      average,
      trend,
      series,
      summary,
    };
  });
}

function startOfWeek(date: Date) {
  const next = new Date(date);
  const day = next.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  next.setHours(0, 0, 0, 0);
  next.setDate(next.getDate() + diff);
  return next;
}

/* ═══ Dashboard ═══ */
export default function Dashboard() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploadStep, setUploadStep] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  /* ── Queries (untouched) ── */
  const {
    data: stats,
    isLoading: statsLoading,
    error: statsError,
    refetch: refetchStats,
  } = useQuery({
    queryKey: queryKeys.stats,
    queryFn: statsApi.overview,
    staleTime: 300000,
  });

  const {
    data: sessionsData,
    isLoading: sessionsLoading,
    error: sessionsError,
    refetch: refetchSessions,
  } = useQuery({
    queryKey: queryKeys.sessions,
    queryFn: sessionsApi.list,
    staleTime: 300000,
  });

  const sessions = sessionsData?.sessions ?? [];
  const cachedTrainingProgress =
    queryClient.getQueryData<TrainingProgressStorePayload | null>(queryKeys.trainingProgress) ??
    null;

  const { data: trainingProgress } = useQuery({
    queryKey: queryKeys.trainingProgress,
    queryFn: async () => (await trainingProgressApi.get())?.progress ?? null,
    initialData: cachedTrainingProgress,
    staleTime: 120000,
  });

  /* ── Mutations (untouched) ── */
  const deleteMutation = useMutation({
    mutationFn: (id: number) => sessionsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sessions });
      queryClient.invalidateQueries({ queryKey: queryKeys.stats });
    },
  });

  /* ── Computed data (untouched) ── */
  const chartData = useMemo(() => {
    if (!sessions.length) return mockBankrollHistory;
    return sessions.slice(-8).map((s, i) => ({
      week: 'Sem ' + (i + 1),
      lucro: Number((s.net_ev_bb ?? 0).toFixed(1)),
      ev: Number((s.stats?.ALL?.vpipNetBb ?? (s.net_ev_bb ?? 0) * 1.1).toFixed(1)),
    }));
  }, [sessions]);

  const insights = useMemo(() => extractInsights(sessions), [sessions]);

  const roi =
    stats && stats.sessions_count > 0 && stats.total_ev_bb != null
      ? ((stats.total_ev_bb / stats.sessions_count) * 10).toFixed(1)
      : '0.0';

  const level = computeLevel(stats?.sessions_count ?? 0);

  const trainingSummary = useMemo(() => {
    if (!trainingProgress) return null;

    const weekStart = startOfWeek(new Date());
    const drillEntries = Object.values(trainingProgress.drills);
    const weeklyHistory = trainingProgress.history.filter(
      (entry) => new Date(entry.timestamp) >= weekStart
    );
    const weeklyReps = weeklyHistory.reduce(
      (sum, entry) => sum + (entry.action === 'repetition' ? entry.value ?? 0 : 0),
      0
    );
    const weeklyBlocks = weeklyHistory.filter((entry) => entry.action === 'completed').length;
    const topDrill =
      drillEntries.sort((a, b) => b.repetitions - a.repetitions)[0]?.drillName ?? 'Sem drill ativo';

    return {
      drillsTracked: drillEntries.length,
      weeklyReps,
      weeklyBlocks,
      topDrill,
      coachLaunches: drillEntries.reduce((sum, item) => sum + item.coachLaunches, 0),
    };
  }, [trainingProgress]);

  const leakConsistency = useMemo(() => buildDashboardLeakTrends(sessions), [sessions]);

  /* ── Upload interval (untouched) ── */
  useEffect(() => {
    if (!isUploading) return;
    let step = 0;
    const iv = setInterval(() => {
      step = Math.min(step + 1, UPLOAD_STEPS.length - 1);
      setUploadStep(step);
    }, 1500);
    return () => clearInterval(iv);
  }, [isUploading]);

  /* ── Handlers (untouched) ── */
  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploading(true);
    setUploadStep(0);
    setUploadError(null);
    try {
      await sessionsApi.analyze(file);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.sessions }),
        queryClient.invalidateQueries({ queryKey: queryKeys.stats }),
      ]);
    } catch (err: unknown) {
      setUploadError(err instanceof Error ? err.message : 'Erro ao analisar arquivo');
    } finally {
      setIsUploading(false);
      setUploadStep(0);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  function evStatus(ev: number) {
    if (ev > 0) return { label: 'Ótimo', color: '#000', bg: '#00e676' };
    if (ev > -5) return { label: 'Revisar', color: '#000', bg: '#f5c800' };
    return { label: 'Crítico', color: '#fff', bg: '#e53935' };
  }

  function evBadge(ev: number) {
    if (ev > 0) return { color: '#00e676', bg: 'rgba(0,230,118,0.10)' };
    if (ev > -5) return { color: '#f5c800', bg: 'rgba(245,200,0,0.10)' };
    return { color: '#e53935', bg: 'rgba(229,57,53,0.10)' };
  }

  const insightDot: Record<string, string> = {
    positive: '#00e676',
    warning: '#f5c800',
    critical: '#e53935',
  };

  const CARD: React.CSSProperties = {
    background: '#1a1a1a',
    border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: 10,
    padding: 24,
  };

  const dotColors = ['#00e676', '#f5c800', '#4da6ff', '#9c6fff'];

  /* ═══ Render ═══ */
  return (
    <PageLayout activeRoute="/dashboard">
      <style>{`
        @keyframes skeleton-pulse { 0%,100%{opacity:1} 50%{opacity:.45} }
      `}</style>

      {/* ── Header ── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          marginBottom: 24,
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div>
          <h1 style={{ color: '#fff', fontSize: 24, fontWeight: 700, margin: 0, ...F }}>
            Dashboard
          </h1>
          <p style={{ color: '#a0a0a0', fontSize: 13, marginTop: 4, ...F }}>
            Bem-vindo de volta,{' '}
            <span style={{ color: '#ffffff' }}>{user?.name ?? '—'}</span>
          </p>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <select
            style={{
              background: '#161616',
              border: '1px solid rgba(255,255,255,0.07)',
              color: '#ffffff',
              borderRadius: 8,
              padding: '10px 14px',
              fontSize: 14,
              cursor: 'pointer',
              outline: 'none',
              ...F,
            }}
          >
            <option>Últimos 30 dias</option>
            <option>Últimos 7 dias</option>
            <option>Últimos 90 dias</option>
          </select>
          <button
            onClick={() => {
              queryClient.invalidateQueries({ queryKey: queryKeys.stats });
              queryClient.invalidateQueries({ queryKey: queryKeys.sessions });
              queryClient.invalidateQueries({ queryKey: queryKeys.trainingProgress });
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              background: 'transparent',
              border: '1px solid rgba(255,255,255,0.2)',
              color: '#ffffff',
              borderRadius: 8,
              padding: '10px 20px',
              fontSize: 14,
              cursor: 'pointer',
              transition: 'all 0.15s',
              ...F,
            }}
            onMouseEnter={(e) => {
              const t = e.currentTarget;
              t.style.borderColor = 'rgba(255,255,255,0.4)';
              t.style.background = 'rgba(255,255,255,0.04)';
            }}
            onMouseLeave={(e) => {
              const t = e.currentTarget;
              t.style.borderColor = 'rgba(255,255,255,0.2)';
              t.style.background = 'transparent';
            }}
          >
            <RefreshCw size={14} />
            Sincronizar
          </button>
        </div>
      </div>

      {/* ── Error banner ── */}
      {(statsError || sessionsError) && (
        <div
          style={{
            background: 'rgba(229,57,53,0.10)',
            border: '1px solid rgba(229,57,53,0.3)',
            borderRadius: 8,
            padding: '12px 16px',
            marginBottom: 16,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            ...F,
          }}
        >
          <span style={{ color: '#e53935', fontSize: 13 }}>
            Erro ao carregar dados. Verifique sua conexão.
          </span>
          <button
            onClick={() => { refetchStats(); refetchSessions(); }}
            style={{
              background: '#e53935',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '8px 16px',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              ...F,
            }}
          >
            Tentar novamente
          </button>
        </div>
      )}

      {uploadError && (
        <div
          style={{
            background: 'rgba(229,57,53,0.10)',
            border: '1px solid rgba(229,57,53,0.3)',
            borderRadius: 8,
            padding: '10px 16px',
            marginBottom: 16,
            fontSize: 13,
            color: '#e53935',
            ...F,
          }}
        >
          {uploadError}
        </div>
      )}

      {/* ══════ 4 Metric Cards ══════ */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 16,
          marginTop: 24,
        }}
      >
        {statsLoading
          ? Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} height={120} />)
          : [
              {
                label: 'WINRATE',
                value: stats?.win_rate != null ? `${stats.win_rate.toFixed(1)}%` : '—',
                change: '+2.3% vs mês anterior',
                positive: true,
              },
              {
                label: 'EV TOTAL',
                value: stats?.total_ev_bb != null ? `${stats.total_ev_bb.toFixed(1)} BB` : '—',
                change: '+12.8% vs mês anterior',
                positive: true,
              },
              {
                label: 'ROI',
                value: `${roi}%`,
                change: '+2.1%',
                positive: true,
              },
              {
                label: 'SESSÕES',
                value: stats?.sessions_count != null ? String(stats.sessions_count) : '—',
                change: '-3 vs mês anterior',
                positive: false,
              },
            ].map((m, i) => (
              <div key={m.label} style={CARD}>
                {/* dot + label */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: dotColors[i],
                      flexShrink: 0,
                    }}
                  />
                  <span
                    style={{
                      color: '#555555',
                      fontSize: 11,
                      fontWeight: 500,
                      letterSpacing: '0.8px',
                      textTransform: 'uppercase',
                      ...F,
                    }}
                  >
                    {m.label}
                  </span>
                </div>
                {/* value */}
                <p
                  style={{
                    color: '#ffffff',
                    fontSize: 32,
                    fontWeight: 700,
                    margin: '8px 0 0',
                    lineHeight: 1.1,
                    ...F,
                  }}
                >
                  {m.value}
                </p>
                {/* change */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    marginTop: 6,
                  }}
                >
                  {m.positive ? (
                    <TrendingUp size={12} color="#00e676" />
                  ) : (
                    <TrendingDown size={12} color="#e53935" />
                  )}
                  <span
                    style={{
                      fontSize: 12,
                      color: m.positive ? '#00e676' : '#e53935',
                      ...F,
                    }}
                  >
                    {m.change}
                  </span>
                </div>
              </div>
            ))}
      </div>

      <div
        style={{
          ...CARD,
          marginTop: 16,
          display: 'flex',
          justifyContent: 'space-between',
          gap: 20,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={{ color: '#555555', fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', ...F }}>
            Treino da Semana
          </div>
          <div style={{ color: '#ffffff', fontSize: 24, fontWeight: 700, marginTop: 8, ...F }}>
            {trainingSummary ? trainingSummary.topDrill : 'Conecte sua rotina de treinos'}
          </div>
          <div style={{ color: '#8c8c8c', fontSize: 13, marginTop: 6, lineHeight: 1.6, ...F }}>
            {trainingSummary
              ? `${trainingSummary.weeklyReps} reps e ${trainingSummary.weeklyBlocks} blocos nesta semana. ${trainingSummary.drillsTracked} drills acompanhados.`
              : 'Assim que voce abrir Treinos, o dashboard passa a refletir reps, blocos e drill mais praticado.'}
          </div>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(96px, 1fr))',
            gap: 10,
            minWidth: 300,
            flex: 1,
          }}
        >
          {[
            {
              label: 'DRILLS',
              value: trainingSummary ? String(trainingSummary.drillsTracked) : '0',
            },
            {
              label: 'REPS',
              value: trainingSummary ? String(trainingSummary.weeklyReps) : '0',
            },
            {
              label: 'COACH',
              value: trainingSummary ? String(trainingSummary.coachLaunches) : '0',
            },
          ].map((item) => (
            <div
              key={item.label}
              style={{
                background: '#161616',
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: 10,
                padding: 14,
              }}
            >
              <div style={{ color: '#555555', fontSize: 10, fontWeight: 600, letterSpacing: '0.08em', ...F }}>
                {item.label}
              </div>
              <div style={{ color: '#fff', fontSize: 24, fontWeight: 700, marginTop: 6, ...F }}>
                {item.value}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ ...CARD, marginTop: 16 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
            marginBottom: 16,
          }}
        >
          <div>
            <div
              style={{
                color: '#fff',
                fontSize: 16,
                fontWeight: 600,
                ...F,
              }}
            >
              Consistência por Leak
            </div>
            <div
              style={{
                color: '#8c8c8c',
                fontSize: 13,
                marginTop: 4,
                ...F,
              }}
            >
              Leitura curta dos leaks mais recorrentes nas últimas sessões carregadas
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <span
              style={{
                background: '#161616',
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: 999,
                padding: '5px 10px',
                color: '#a0a0a0',
                fontSize: 11,
                ...F,
              }}
            >
              janela {Math.min(5, sessions.length || 0)} sessões
            </span>
            <span
              style={{
                background: 'rgba(0,230,118,0.08)',
                border: '1px solid rgba(0,230,118,0.18)',
                borderRadius: 999,
                padding: '5px 10px',
                color: '#00e676',
                fontSize: 11,
                ...F,
              }}
            >
              leitura dashboard
            </span>
          </div>
        </div>

        {sessionsLoading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            {[1, 2, 3].map((i) => <SkeletonCard key={i} height={120} />)}
          </div>
        ) : leakConsistency.length === 0 ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '24px 0 8px',
              gap: 8,
            }}
          >
            <Lightbulb size={24} color="#333" />
            <div style={{ color: '#555', fontSize: 14, ...F }}>
              Faça upload de sessões para acompanhar a consistência dos leaks.
            </div>
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              gap: 12,
            }}
          >
            {leakConsistency.map((leak) => {
              const tone =
                leak.trend === 'improved'
                  ? '#00e676'
                  : leak.trend === 'worse'
                  ? '#e53935'
                  : '#a0a0a0';

              return (
                <Link
                  key={leak.id}
                  to="/sessions"
                  state={{ focusLeakId: leak.id, source: 'dashboard-consistency' }}
                  style={{
                    textDecoration: 'none',
                    background: '#161616',
                    border: '1px solid rgba(255,255,255,0.06)',
                    borderRadius: 10,
                    padding: 14,
                    display: 'block',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 8,
                      alignItems: 'flex-start',
                    }}
                  >
                    <div>
                      <div style={{ color: '#fff', fontSize: 14, fontWeight: 600, ...F }}>
                        {leak.label}
                      </div>
                      <div
                        style={{
                          marginTop: 6,
                          color: '#8c8c8c',
                          fontSize: 12,
                          lineHeight: 1.5,
                          ...F,
                        }}
                      >
                        {leak.summary}
                      </div>
                    </div>
                    <div style={{ color: tone, fontSize: 18, fontWeight: 700, ...F }}>
                      {leak.average.toFixed(1)}
                    </div>
                  </div>

                  <div style={{ width: '100%', height: 44, marginTop: 12 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={leak.series}>
                        <Line
                          type="monotone"
                          dataKey="cost"
                          stroke={tone}
                          strokeWidth={2}
                          dot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  <div
                    style={{
                      marginTop: 8,
                      color: '#555',
                      fontSize: 11,
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      ...F,
                    }}
                  >
                    Impacto médio em BB
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* ══════ Row 2: Chart + Insights ══════ */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr',
          gap: 16,
          marginTop: 16,
        }}
      >
        {/* ── Bankroll Chart ── */}
        <div style={{ ...CARD, padding: 24 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 16,
            }}
          >
            <span style={{ color: '#fff', fontSize: 16, fontWeight: 600, ...F }}>
              Evolução do Bankroll
            </span>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, ...F }}>
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: '#00e676',
                    display: 'inline-block',
                  }}
                />
                <span style={{ color: '#a0a0a0', fontSize: 12 }}>Lucro</span>
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, ...F }}>
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: '#f5c800',
                    display: 'inline-block',
                  }}
                />
                <span style={{ color: '#a0a0a0', fontSize: 12 }}>EV</span>
              </span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart
              data={chartData}
              margin={{ top: 5, right: 10, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id="gradientLucro" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#00e676" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#00e676" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gradientEV" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f5c800" stopOpacity={0.08} />
                  <stop offset="95%" stopColor="#f5c800" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="rgba(255,255,255,0.04)"
                vertical={false}
              />
              <XAxis
                dataKey="week"
                stroke="#333"
                tick={{ fill: '#555', fontSize: 11, fontFamily: 'Inter' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                stroke="#333"
                tick={{ fill: '#555', fontSize: 11, fontFamily: 'Inter' }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<ChartTooltip />} />
              <Area
                type="monotone"
                dataKey="lucro"
                name="Lucro"
                stroke="#00e676"
                strokeWidth={2}
                fill="url(#gradientLucro)"
                dot={{ fill: '#00e676', r: 3, strokeWidth: 0 }}
                activeDot={{ r: 5, strokeWidth: 0 }}
              />
              <Area
                type="monotone"
                dataKey="ev"
                name="EV"
                stroke="#f5c800"
                strokeWidth={1.5}
                strokeDasharray="4 4"
                fill="url(#gradientEV)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* ── AI Insights ── */}
        <div style={{ ...CARD, padding: 24 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 16,
            }}
          >
            <Brain size={16} color="#00e676" />
            <span style={{ color: '#fff', fontSize: 16, fontWeight: 600, ...F }}>
              Insights da IA
            </span>
          </div>
          {sessionsLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[1, 2, 3].map((i) => <SkeletonCard key={i} height={52} />)}
            </div>
          ) : insights.length === 0 ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '32px 0',
                gap: 8,
              }}
            >
              <Lightbulb size={24} color="#333" />
              <p style={{ color: '#555', fontSize: 13, ...F }}>Faça upload de mãos</p>
              <p style={{ color: '#555', fontSize: 13, ...F }}>para ver insights</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {insights.map((ins, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    gap: 10,
                    alignItems: 'flex-start',
                    padding: '10px 0',
                  }}
                >
                  <div
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      background: insightDot[ins.severity],
                      flexShrink: 0,
                      marginTop: 6,
                    }}
                  />
                  <p
                    style={{
                      color: '#a0a0a0',
                      fontSize: 13,
                      lineHeight: 1.5,
                      margin: 0,
                      ...F,
                    }}
                  >
                    {ins.text}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ══════ Row 3: Recent Sessions + Level ══════ */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr',
          gap: 16,
          marginTop: 16,
        }}
      >
        {/* ── Recent Sessions ── */}
        <div style={{ ...CARD, padding: 24 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 16,
            }}
          >
            <span style={{ color: '#fff', fontSize: 16, fontWeight: 600, ...F }}>
              Análise Recente de Mãos
            </span>
            <Link
              to="/sessions"
              style={{
                color: '#00e676',
                fontSize: 13,
                textDecoration: 'none',
                cursor: 'pointer',
                ...F,
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.textDecoration = 'underline';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.textDecoration = 'none';
              }}
            >
              Ver todas
            </Link>
          </div>

          {/* Hidden file input + upload button */}
          <input
            ref={fileRef}
            type="file"
            accept=".txt"
            style={{ display: 'none' }}
            onChange={handleFile}
          />

          {sessionsLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[1, 2, 3].map((i) => <SkeletonCard key={i} height={56} />)}
            </div>
          ) : sessions.length === 0 ? (
            /* ── Empty state ── */
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '32px 0',
                gap: 4,
              }}
            >
              <Upload size={32} color="#333" />
              <p style={{ color: '#555', fontSize: 15, marginTop: 12, ...F }}>
                Nenhuma mão analisada ainda
              </p>
              <p style={{ color: '#555', fontSize: 13, ...F }}>
                Faça upload do seu histórico .txt do GGPoker
              </p>
              <button
                onClick={() => fileRef.current?.click()}
                style={{
                  marginTop: 16,
                  background: 'transparent',
                  border: '1px solid #00e676',
                  color: '#00e676',
                  borderRadius: 8,
                  padding: '10px 20px',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'background 0.15s',
                  ...F,
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = 'rgba(0,230,118,0.08)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                }}
              >
                Carregar minha primeira mão
              </button>
            </div>
          ) : isUploading ? (
            /* ── Upload state ── */
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 12,
                padding: '32px 0',
              }}
            >
              <RefreshCw
                size={18}
                color="#00e676"
                style={{ animation: 'spin 1s linear infinite' }}
              />
              <span style={{ color: '#a0a0a0', fontSize: 14, ...F }}>
                {UPLOAD_STEPS[uploadStep]}
              </span>
              <style>{`@keyframes spin { from{transform:rotate(0)} to{transform:rotate(360deg)} }`}</style>
            </div>
          ) : (
            /* ── Session list ── */
            <div>
              {sessions.slice(0, 5).map((s: Session, idx: number) => {
                const ev = s.net_ev_bb ?? 0;
                const st = evStatus(ev);
                const eb = evBadge(ev);
                const fname =
                  (s.filename ?? '').length > 20
                    ? s.filename.slice(0, 20) + '…'
                    : s.filename ?? '—';
                const isLast = idx === Math.min(sessions.length, 5) - 1;
                return (
                  <div
                    key={s.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '14px 0',
                      borderBottom: isLast ? 'none' : '1px solid rgba(255,255,255,0.05)',
                      gap: 12,
                    }}
                  >
                    {/* Left: date + filename */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ color: '#a0a0a0', fontSize: 13, ...F }}>{s.date}</span>
                      <span
                        style={{
                          color: '#ffffff',
                          fontSize: 13,
                          fontWeight: 500,
                          marginLeft: 12,
                          ...F,
                        }}
                      >
                        {fname}
                      </span>
                    </div>

                    {/* Center: stakes badge */}
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                      {s.stakes && (
                        <span
                          style={{
                            background: '#2a2a2a',
                            color: '#888',
                            borderRadius: 4,
                            padding: '2px 8px',
                            fontSize: 13,
                            ...F,
                          }}
                        >
                          {s.stakes}
                        </span>
                      )}

                      {/* EV badge */}
                      <span
                        style={{
                          background: eb.bg,
                          color: eb.color,
                          borderRadius: 6,
                          padding: '4px 10px',
                          fontSize: 13,
                          fontWeight: 600,
                          ...F,
                        }}
                      >
                        {ev >= 0 ? '+' : ''}
                        {ev.toFixed(1)} BB
                      </span>

                      {/* Status badge */}
                      <span
                        style={{
                          background: st.bg,
                          color: st.color,
                          borderRadius: 6,
                          padding: '4px 10px',
                          fontSize: 12,
                          fontWeight: 600,
                          ...F,
                        }}
                      >
                        {st.label}
                      </span>

                      {/* Delete */}
                      <button
                        onClick={() => deleteMutation.mutate(s.id)}
                        disabled={deleteMutation.isPending}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: 4,
                          color: '#555',
                          display: 'flex',
                          lineHeight: 0,
                          transition: 'color 0.15s',
                        }}
                        onMouseEnter={(e) => {
                          (e.currentTarget as HTMLButtonElement).style.color = '#e53935';
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as HTMLButtonElement).style.color = '#555';
                        }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}

              {/* Upload button below list */}
              <div style={{ marginTop: 12 }}>
                <button
                  onClick={() => fileRef.current?.click()}
                  disabled={isUploading}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    background: '#00e676',
                    color: '#000',
                    border: 'none',
                    borderRadius: 8,
                    padding: '12px 24px',
                    fontWeight: 700,
                    fontSize: 13,
                    cursor: isUploading ? 'not-allowed' : 'pointer',
                    opacity: isUploading ? 0.7 : 1,
                    transition: 'background 0.15s',
                    ...F,
                  }}
                  onMouseEnter={(e) => {
                    if (!isUploading)
                      (e.currentTarget as HTMLButtonElement).style.background = '#00c060';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background = '#00e676';
                  }}
                >
                  <Upload size={14} />
                  {isUploading ? UPLOAD_STEPS[uploadStep] : 'Carregar Mão'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── Player Level ── */}
        <div style={{ ...CARD, padding: 24, display: 'flex', flexDirection: 'column' }}>
          <span style={{ color: '#fff', fontSize: 16, fontWeight: 600, marginBottom: 16, ...F }}>
            Nível do Jogador
          </span>

          {/* Circle SVG */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              marginBottom: 16,
            }}
          >
            <svg viewBox="0 0 90 90" width={90} height={90}>
              <circle
                cx="45"
                cy="45"
                r="38"
                fill="none"
                stroke="#2a2a2a"
                strokeWidth={6}
              />
              <circle
                cx="45"
                cy="45"
                r="38"
                fill="none"
                stroke="#00e676"
                strokeWidth={6}
                strokeLinecap="round"
                strokeDasharray={`${2 * Math.PI * 38}`}
                strokeDashoffset={`${
                  2 * Math.PI * 38 * (1 - level.xpCurrent / level.xpRequired)
                }`}
                transform="rotate(-90 45 45)"
                style={{ transition: 'stroke-dashoffset 0.6s ease' }}
              />
              <text
                x="45"
                y="50"
                textAnchor="middle"
                fill="#00e676"
                fontSize="22"
                fontWeight="700"
                fontFamily="Inter, sans-serif"
              >
                {level.level}
              </text>
            </svg>
            <p style={{ color: '#a0a0a0', fontSize: 12, marginTop: 8, ...F }}>
              {level.label}
            </p>
          </div>

          {/* XP bar */}
          <div style={{ marginTop: 'auto' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: 6,
              }}
            >
              <span
                style={{
                  color: '#555555',
                  fontSize: 11,
                  fontWeight: 500,
                  letterSpacing: '0.8px',
                  textTransform: 'uppercase',
                  ...F,
                }}
              >
                XP
              </span>
              <span style={{ color: '#00e676', fontSize: 11, ...F }}>
                {level.xpCurrent} / {level.xpRequired}
              </span>
            </div>
            <div
              style={{
                height: 6,
                background: '#2a2a2a',
                borderRadius: 4,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${(level.xpCurrent / level.xpRequired) * 100}%`,
                  background: '#00e676',
                  borderRadius: 4,
                  transition: 'width 0.4s ease',
                }}
              />
            </div>

            {/* Labels */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: 8,
              }}
            >
              {['Iniciante', 'Intermediário', 'Avançado'].map((lbl) => (
                <span
                  key={lbl}
                  style={{
                    fontSize: 11,
                    color: lbl === level.label ? '#00e676' : '#555555',
                    fontWeight: lbl === level.label ? 600 : 400,
                    ...F,
                  }}
                >
                  {lbl}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}
